import { useState, useEffect, createContext, useContext } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { Toaster } from "@/components/ui/sonner";
import { toast } from "sonner";

// Pages
import LoginPage from "@/pages/LoginPage";
import DashboardPage from "@/pages/DashboardPage";
import StaffListPage from "@/pages/StaffListPage";
import StaffDetailPage from "@/pages/StaffDetailPage";
import ReportsPage from "@/pages/ReportsPage";
import SettingsPage from "@/pages/SettingsPage";
import BulkUploadPage from "@/pages/BulkUploadPage";
import MedicalDashboardPage from "@/pages/MedicalDashboardPage";
import MedicalPersonListPage from "@/pages/MedicalPersonListPage";
import MedicalPersonDetailPage from "@/pages/MedicalPersonDetailPage";
import MedicalBulkUploadPage from "@/pages/MedicalBulkUploadPage";
import MedicalUnmatchedPage from "@/pages/MedicalUnmatchedPage";

// Layout
import AppLayout from "@/components/AppLayout";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// Auth Context
const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [token, setToken] = useState(localStorage.getItem("token"));
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const checkAuth = async () => {
      if (token) {
        try {
          const response = await axios.get(`${API}/auth/me`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          setUser(response.data);
        } catch (error) {
          // Only clear auth on 401 (unauthorized) - not network errors or 500s
          if (error.response && error.response.status === 401) {
            localStorage.removeItem("token");
            setToken(null);
            setUser(null);
          }
        }
      }
      setLoading(false);
    };
    checkAuth();
  }, [token]);

  const login = async (username, password) => {
    try {
      const response = await axios.post(`${API}/auth/login`, {
        username,
        password
      });
      const { access_token } = response.data;
      localStorage.setItem("token", access_token);
      setToken(access_token);
      
      // Get user info
      const userResponse = await axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${access_token}` }
      });
      setUser(userResponse.data);
      
      toast.success("Login successful");
      return true;
    } catch (error) {
      toast.error(error.response?.data?.detail || "Invalid credentials");
      return false;
    }
  };

  const logout = () => {
    localStorage.removeItem("token");
    setToken(null);
    setUser(null);
    toast.success("Logged out successfully");
  };

  const changePassword = async (currentPassword, newPassword) => {
    try {
      await axios.post(`${API}/auth/change-password`, {
        current_password: currentPassword,
        new_password: newPassword
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Password changed successfully");
      return true;
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to change password");
      return false;
    }
  };

  return (
    <AuthContext.Provider value={{ token, user, loading, login, logout, changePassword }}>
      {children}
    </AuthContext.Provider>
  );
};

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { token, loading, user } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-navy"></div>
      </div>
    );
  }

  if (!token) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Medical-only users cannot access cert pages — bounce them to medical
  if (user?.role === "medical") {
    const path = location.pathname;
    const isMedicalPath = path.startsWith("/medical") || path === "/settings";
    if (!isMedicalPath) {
      return <Navigate to="/medical/dashboard" replace />;
    }
  }

  return children;
};

// Smart index redirect based on role
const IndexRedirect = () => {
  const { user } = useAuth();
  if (user?.role === "medical") {
    return <Navigate to="/medical/dashboard" replace />;
  }
  return <Navigate to="/dashboard" replace />;
};

// Smart catch-all redirect based on role
const CatchAllRedirect = () => {
  const { user, token } = useAuth();
  if (!token) {
    return <Navigate to="/login" replace />;
  }
  if (user?.role === "medical") {
    return <Navigate to="/medical/dashboard" replace />;
  }
  return <Navigate to="/dashboard" replace />;
};

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Routes>
          <Route path="/login" element={<LoginPage />} />
          <Route
            path="/"
            element={
              <ProtectedRoute>
                <AppLayout />
              </ProtectedRoute>
            }
          >
            <Route index element={<IndexRedirect />} />
            <Route path="dashboard" element={<DashboardPage />} />
            <Route path="staff" element={<StaffListPage />} />
            <Route path="staff/:staffId" element={<StaffDetailPage />} />
            <Route path="bulk-upload" element={<BulkUploadPage />} />
            <Route path="reports" element={<ReportsPage />} />
            <Route path="settings" element={<SettingsPage />} />
            {/* Medical System Routes */}
            <Route path="medical" element={<Navigate to="/medical/dashboard" replace />} />
            <Route path="medical/dashboard" element={<MedicalDashboardPage />} />
            <Route path="medical/persons" element={<MedicalPersonListPage />} />
            <Route path="medical/persons/:personId" element={<MedicalPersonDetailPage />} />
            <Route path="medical/bulk-upload" element={<MedicalBulkUploadPage />} />
            <Route path="medical/unmatched" element={<MedicalUnmatchedPage />} />
          </Route>
          <Route path="*" element={<CatchAllRedirect />} />
        </Routes>
        <Toaster position="top-right" richColors />
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
